export interface ThemeVocabulary {
  points: string;
  daily: string;
  total: string;
  goal: string;
  activities: string;
  streak: string;
  progress: string;
  earned: string;
  remaining: string;
  
  // LEGO-specific vocabulary (optional fields)
  weeklyLog?: string;
  perfectDays?: string;
  activeDays?: string;
  levels?: string;
  noActivity?: string;
}

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  cardBackground: string;
  border: string;
  
  // LEGO-specific colors (optional)
  container?: string;
  button?: string;
  currentButton?: string;
}

// NEW: LEGO Block Configuration
export interface LegoBlockConfig {
  enabled: boolean; // Whether to show LEGO blocks or regular progress bars
  blockColors: {
    empty: string;
    low: string;      // 1-29 pts
    medium: string;   // 30-59 pts 
    high: string;     // 60-79 pts
    excellent: string; // 80+ pts
  };
  labels: {
    empty: string;
    low: string;
    medium: string;
    high: string;
    excellent: string;
  };
}

export interface ThemeComponentConfig {
  showActivityGrid: boolean;
  showWeeklyHistory: boolean;
  showDetailedStats: boolean;
  progressVisualization: 'thermometer' | 'tower' | 'belt' | 'assembly';
  customComponents?: string[];
  maxDisplayMetrics: number;
  
  // NEW: LEGO block configuration
  legoBlocks?: LegoBlockConfig;
}

export interface ThemeConfig {
  name: string;
  displayName: string;
  description: string;
  targetAudience: 'child' | 'parent' | 'both';
  vocabulary: ThemeVocabulary;
  colors: ThemeColors;
  components: ThemeComponentConfig;
  animations: {
    enabled: boolean;
    intensity: 'low' | 'medium' | 'high';
  };
  adhdOptimized: boolean;
}

export type ThemeName = 'default' | 'lego-classic' | 'lego-ninjago';

// Theme context interface - MOVED HERE from context file
export interface ThemeContextType {
  currentTheme: ThemeName;
  themeConfig: ThemeConfig;
  setTheme: (theme: ThemeName) => void;
  getVocabulary: (key: keyof ThemeVocabulary) => string;
  getColor: (path: string) => string;
  isChildTheme: () => boolean;
  isAdhdOptimized: () => boolean;
}

// UPDATED THEME_CONFIGS with LEGO block configurations
export const THEME_CONFIGS: Record<ThemeName, ThemeConfig> = {
  default: {
    name: 'default',
    displayName: 'Nintendo Quest',
    description: 'Complete analytics and detailed tracking',
    targetAudience: 'parent',
    vocabulary: {
      points: 'Points',
      daily: 'Daily Points',
      total: 'Total Points',
      goal: 'Nintendo Switch Goal',
      activities: 'Activities',
      streak: 'Streak',
      progress: 'Progress',
      earned: 'Earned',
      remaining: 'Remaining',
      weeklyLog: '📅 Weekly Activities',
      perfectDays: 'perfect days',
      activeDays: 'active days',
      levels: '📊 Point Levels:',
      noActivity: 'No Activity'
    },
    colors: {
      primary: 'indigo-600',
      secondary: 'purple-600',
      accent: 'pink-500',
      background: 'gradient-to-br from-indigo-50 via-white to-purple-50',
      text: 'gray-900',
      cardBackground: 'white',
      border: 'gray-200',
      container: 'bg-white border border-gray-200',
      button: 'bg-indigo-600 hover:bg-indigo-700 border-indigo-600',
      currentButton: 'bg-purple-600 hover:bg-purple-700 border-purple-600'
    },
    components: {
      showActivityGrid: true,
      showWeeklyHistory: true,
      showDetailedStats: true,
      progressVisualization: 'thermometer',
      maxDisplayMetrics: 12,
      legoBlocks: {
        enabled: false, // Default theme uses regular progress bars
        blockColors: {
          empty: 'bg-gray-200 border-gray-300',
          low: 'bg-indigo-200 border-indigo-300',
          medium: 'bg-indigo-400 border-indigo-500',
          high: 'bg-indigo-600 border-indigo-700',
          excellent: 'bg-gradient-to-t from-indigo-600 to-purple-500 border-purple-500'
        },
        labels: {
          empty: 'No Activity',
          low: 'Basic Progress',
          medium: 'Good Progress',
          high: 'Excellent Progress',
          excellent: 'Outstanding!'
        }
      }
    },
    animations: {
      enabled: true,
      intensity: 'medium'
    },
    adhdOptimized: false
  },

  'lego-classic': {
    name: 'lego-classic',
    displayName: 'LEGO Master Builder',
    description: 'Fun building theme with simplified metrics',
    targetAudience: 'child',
    vocabulary: {
      points: 'Bricks',
      daily: "Today's Bricks",
      total: 'Total Bricks',
      goal: 'Nintendo Switch LEGO Set',
      activities: 'Building Tasks',
      streak: 'Building Streak',
      progress: 'Construction Progress',
      earned: 'Bricks Collected',
      remaining: 'Bricks Needed',
      weeklyLog: '🏗️ Weekly Building Log',
      perfectDays: 'perfect building days',
      activeDays: 'active building days',
      levels: '🧱 Building Levels:',
      noActivity: 'No Building'
    },
    colors: {
      primary: 'red-500',
      secondary: 'yellow-500',
      accent: 'blue-500',
      background: 'gradient-to-br from-red-100 via-yellow-100 to-blue-100',
      text: 'slate-800',
      cardBackground: 'white',
      border: 'slate-300',
      container: 'bg-gradient-to-br from-red-100 via-yellow-100 to-blue-100 border-2 border-red-300',
      button: 'bg-red-500 hover:bg-red-600 border-red-400',
      currentButton: 'bg-blue-500 hover:bg-blue-600 border-blue-400'
    },
    components: {
      showActivityGrid: false,
      showWeeklyHistory: true,
      showDetailedStats: false,
      progressVisualization: 'tower',
      customComponents: ['LegoBrickInventory', 'BuildingTimeline'],
      maxDisplayMetrics: 4,
      legoBlocks: {
        enabled: true, // LEGO Classic uses actual LEGO blocks
        blockColors: {
          empty: 'bg-gray-300 border-gray-400',
          low: 'bg-gradient-to-t from-red-500 to-red-400 border-red-400',
          medium: 'bg-gradient-to-t from-yellow-500 to-yellow-400 border-yellow-400',
          high: 'bg-gradient-to-t from-green-500 to-green-400 border-green-400',
          excellent: 'bg-gradient-to-t from-blue-500 to-blue-400 border-blue-400'
        },
        labels: {
          empty: 'No Building',
          low: 'Basic Building',
          medium: 'Good Building',
          high: 'Excellent Building',
          excellent: 'Master Building!'
        }
      }
    },
    animations: {
      enabled: true,
      intensity: 'high'
    },
    adhdOptimized: true
  },

  'lego-ninjago': {
    name: 'lego-ninjago',
    displayName: 'Ninjago Training',
    description: 'Epic ninja adventure theme',
    targetAudience: 'child',
    vocabulary: {
      points: 'Energy',
      daily: "Today's Energy",
      total: 'Lightning Energy',
      goal: 'Golden Nintendo Switch',
      activities: 'Training Disciplines',
      streak: 'Spinjitzu Streak',
      progress: 'Ninja Belt Level',
      earned: 'Energy Collected',
      remaining: 'Energy Needed',
      weeklyLog: '🏯 Weekly Training Log',
      perfectDays: 'perfect training days',
      activeDays: 'active training days',
      levels: '⚡ Energy Levels:',
      noActivity: 'No Training'
    },
    colors: {
      primary: 'red-600',
      secondary: 'orange-500',
      accent: 'yellow-400',
      background: 'gradient-to-br from-red-900 via-orange-900 to-yellow-900',
      text: 'orange-100',
      cardBackground: 'black/50',
      border: 'orange-500/50',
      container: 'bg-gradient-to-br from-red-900/20 via-orange-900/20 to-yellow-900/20 border-2 border-orange-500/50',
      button: 'bg-red-600/80 hover:bg-red-600 border-red-500',
      currentButton: 'bg-orange-500/80 hover:bg-orange-500 border-orange-400'
    },
    components: {
      showActivityGrid: false,
      showWeeklyHistory: false,
      showDetailedStats: false,
      progressVisualization: 'belt',
      customComponents: ['ElementalPowers', 'NinjaBelt', 'DojoTraining'],
      maxDisplayMetrics: 4,
      legoBlocks: {
        enabled: true, // Ninjago uses energy-themed blocks
        blockColors: {
          empty: 'bg-slate-800 border-slate-700',        // Dark gray - clearly empty
          low: 'bg-red-500 border-red-400',             // Pure red - easily visible
          medium: 'bg-amber-500 border-amber-400',      // Amber/orange - distinct from red
          high: 'bg-emerald-500 border-emerald-400',    // Green - completely different
          excellent: 'bg-purple-600 border-purple-500'  // Purple - royal master level
        },
        labels: {
          empty: 'No Training',
          low: 'Basic Training',
          medium: 'Good Training',
          high: 'Excellent Training',
          excellent: 'Master Training!'
        }
      }
    },
    animations: {
      enabled: true,
      intensity: 'high'
    },
    adhdOptimized: true
  }
};